import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Box, Typography } from '@mui/material';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { useDateRange } from '../../components/DateRangeProvider';
import { I18nNamespace } from '~/common/types';
import { CubeChart, CubeChartType } from '~/components/CubeChart';
import { monthsInYearFormatter } from '~/components/CubeChart/CubeChart.common';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { colorFromStatus } from '~/util/conversion';
import { computeChangeRate, convertDateRangeToTimeDimension, getSegments, getStatus } from '~/util/cube';

const formatChange = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);
const formatValueCompact = formatNumber(NumberFormatPreset.INTEGER_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.INTEGER);

export const GeneralEmissionWidget = () => {
  const { t } = useTranslation(I18nNamespace.widgets, {
    keyPrefix: 'GeneralEmissionWidget',
  });
  const [widgetRef, active] = useScrollIntoView({ hash: 'general-emission' });
  const { currentDateRange, referenceDateRange } = useDateRange();
  const cube = 'GeneralEmissions';

  const filters: Filter[] = [
    {
      member: `${cube}.countryCode`,
      operator: 'equals',
      values: ['DE'], // Emissions are only available for EU states
    },
  ];
  const emissionSeries = useCubeQuery({
    measures: [`${cube}.totalEmissionValue`],
    timeDimensions: convertDateRangeToTimeDimension(`${cube}.date`, currentDateRange, 'month'),

    filters,
  });

  const emissionSeriesPrevious = useCubeQuery({
    measures: [`${cube}.totalEmissionValue`],
    timeDimensions: convertDateRangeToTimeDimension(`${cube}.date`, referenceDateRange, 'month'),
    filters,
  });

  const { segments, totalValue: currentYear } = getSegments(emissionSeries);
  const { totalValue: previousYear } = getSegments(emissionSeriesPrevious);
  const changeRate = computeChangeRate(currentYear, previousYear);
  const status = getStatus(changeRate, true);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={emissionSeries.isLoading || emissionSeriesPrevious.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatValueCompact(currentYear)}
      valueTooltip={formatValue(currentYear)}
      data-intercom-target="GeneralEmissionWidget"
      referenceValue={
        <QuickViewReferenceValue
          description={formatValueCompact(previousYear)}
          value={formatChange(changeRate)}
          dotColor={colorFromStatus(status)}
        />
      }
    >
      {segments && currentYear ? (
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0',
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              mb: '-5px',
              mr: 3,
            }}
          >
            <Typography component="div" variant="caption" color="textPrimary">
              {t('chartCaption')}
            </Typography>
          </Box>
          <CubeChart
            type={CubeChartType.tinyBar}
            formatter={{
              x: monthsInYearFormatter,
              y: formatValueCompact,
            }}
            {...emissionSeries}
          />
        </Box>
      ) : (
        <PlaceholderWidget />
      )}
    </QuickViewWidget>
  );
};
